import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import type { ApiResponse, IdNamePair, PaginatedResponse } from "@/types/global";
import type {
  ChapterChildrenInputs,
  ChapterChildrenNestedDisplay,
  ChapterTopicConceptsNestedDisplay,
  ChapterTopicMappingDisplay,
  ChapterTopicMappingInputs,
  ChapterWiseQuestionCounts,
  ContentData,
  ContentErrorReportInput,
  CourseDetailsDisplay,
  CourseSubjectMappingDisplay,
  CourseSubjectMappingInputs,
  QuestionByChapterConcept,
  QuestionCount,
  QuestionCountsForContentInputs,
  QuestionSearchResultDisplay,
  QuestionTypesForContentInputs,
  SubjectChapterMappingDisplay,
  SubjectChapterMappingInputs,
  SubjectChildrenInputs,
  SubjectChildrenNestedDisplay,
  TopicConcept,
  TopicConceptMappingDisplay,
  TopicConceptMappingInputs,
} from "@/types/content";
import type {
  ChapterDesignDisplay,
  ChapterMappingSearchDisplay,
  ConceptDesignDisplay,
  ConceptMappingSearchDisplay,
  ConceptDetails,
  CourseDesignDisplay,
  LectureDesignDetailDisplay,
  LectureDesignDisplay,
  QuestionDesignDisplay,
  SubjectDesignDisplay,
  SubjectMappingSearchDisplay,
  TeachingAidDesignDisplay,
  TopicDesignDisplay,
  TopicMappingSearchDisplay,
  TopicDetails,
  VideoDesignDisplay,
} from "@/types/contentDesign";
import type {
  ChapterMappingSearchQuery,
  ConceptMappingSearchQuery,
  ContentDesignSearchQuery,
  LectureDesignSearchQuery,
  SubjectMappingSearchQuery,
  TopicMappingSearchQuery,
  QuestionDesignSearchQuery,
  TeachingAidDesignSearchQuery,
  VideoDesignSearchQuery,
} from "@/types/contentV2Versioning";

/**
 * content-read (`/v7/content-read`) — content-v2 READ surface.
 * Phase C: academic-session course library + content error-report counts,
 * split out of `contentApi.ts` with corrected URLs (legacy `content/...` prefix removed).
 * OLD→NEW mapping: `plans/api-remediation/_agent/phase-c-migration-map.md`.
 */
export default class ContentReadApi extends ApiVerified {
  /**
   * Plain course Id/Name list — the content-read replacement for the removed
   * `content/get-all-content-by-type?contentType=Course`. `onlyActive` defaults true.
   * 200 + IdNamePair[].
   */
  async getAllCourses(onlyActive = true): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(`content-read/courses`, {
        params: { onlyActive: onlyActive },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * Distinct course names across every academic session — the same course repeats once per
   * session in `getAllCourses`, so the course-name pickers read this instead. `onlyActive`
   * defaults true. 200 + string[].
   */
  async getDistinctCourseNames(onlyActive = true): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<string>>(
        `content-read/courses/distinct-names`,
        { params: { onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * Course Library list. `academicSessionId` optional server-side (with it → one
   * session; without → all). 200 + CourseDetailsDisplay[]; empty session → [] (NOT 404).
   */
  async getCoursesForAcademicSession(academicSessionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<CourseDetailsDisplay>>(
        `content-read/academic-sessions/${academicSessionId}/courses`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // One course by id (name + academic session + counts) — the reload-safe header source for the
  // Content Mapping screen, resolvable from the route's courseId alone. 200 + CourseDetailsDisplay.
  async getCourseDetails(courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<CourseDetailsDisplay>(
        `content-read/courses/${courseId}/details`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // No params; list[IdNamePair] with the open count embedded in `Name`. 404 if none.
  async getContentErrorReportCounts(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/error-reports/counts`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Files a content error report (moved off legacy content/submit-content-error-report).
  // The reporter is resolved server-side from the JWT. 200 + true.
  async submitContentErrorReport(
    contentErrorReportInput: ContentErrorReportInput,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `content-read/error-reports`,
        contentErrorReportInput,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // All subject-chapters (optionally scoped to a course) as IdNamePair[].
  async getAllSubjectChapters(courseId: number | null): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/subject-chapters`,
        { params: { courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Question counts per type for a content selection (junction ids).
  async getQuestionCountsForContent(inputs: QuestionCountsForContentInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<QuestionCount>>(
        `content-read/question-counts`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Question type ids available for a content selection (junction ids).
  async getQuestionTypesForContent(inputs: QuestionTypesForContentInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<number>>(
        `content-read/question-types-for-content`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Subjects for a course as IdNamePair[] where Id = CourseSubjectId — feeds the
  // ContentRadioTree `subjects` prop (the fixed Subjects › Chapters › Topics selector).
  async getCourseSubjects(courseId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/courses/${courseId}/subjects`,
        { params: { onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Chapter-wise question counts for ONE course subject (the id from the
  // courses/{id}/subjects read) — one row per (chapter, question type).
  async getCourseWiseQuestionCounts(
    courseId: number,
    courseSubjectId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ChapterWiseQuestionCounts>>(
        `content-read/courses/${courseId}/question-counts`,
        { params: { courseSubjectId: courseSubjectId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Chapters holding questions in one display status — IdNamePair rows with the count
  // embedded in Name (Fix Chapter Questions › status tab). Server default onlyActive=true.
  async getChapterwiseQuestionCountsByStatus(displayStatusId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/chapter-question-counts/by-status`,
        { params: { displayStatusId: displayStatusId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Chapters with open error reports of one type (Fix Chapter Questions › error-reports tab).
  async getChapterwiseQuestionCountsByErrorReports(errorTypeId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/chapter-question-counts/by-error-reports`,
        { params: { errorTypeId: errorTypeId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Chapters holding questions of one type (Fix Chapter Questions › question-types tab).
  async getChapterwiseQuestionCountsByQuestionType(questionTypeId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/chapter-question-counts/by-question-type`,
        { params: { questionTypeId: questionTypeId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Flat subjects mapped into a set of courses (POST content-read/courses/subject-mappings). Each
  // row is a CourseSubject junction: CourseSubjectId + master SubjectId/SubjectName + design +
  // TotalChapters + DisplayRank + IsActive. Course-scoped, no affiliation.
  async getCourseSubjectMappings(inputs: CourseSubjectMappingInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<CourseSubjectMappingDisplay>>(
        `content-read/courses/subject-mappings`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Flat chapters mapped under a set of CourseSubjectIds (POST content-read/subjects/chapter-
  // mappings). Rows carry SubjectChapterId (junction) + Chapter/Class/Module + TotalTopics +
  // DisplayRank + IsActive, but NO CourseSubjectId back-ref — call per subject when the grouping
  // matters. Course-scoped, no affiliation.
  async getSubjectChapterMappings(inputs: SubjectChapterMappingInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<SubjectChapterMappingDisplay>>(
        `content-read/subjects/chapter-mappings`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Flat topics mapped under a set of SubjectChapterIds (POST content-read/chapters/topic-
  // mappings). Rows carry ChapterTopicId (junction) + TopicDesignId/DisplayRank/IsActive/
  // TotalConcepts. Course-scoped (admin content mapping) — no affiliation.
  async getChapterTopicMappings(inputs: ChapterTopicMappingInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<ChapterTopicMappingDisplay>>(
        `content-read/chapters/topic-mappings`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Flat concepts mapped under a set of ChapterTopicIds (POST content-read/topics/concept-
  // mappings). Rows carry TopicConceptId (junction) + ConceptDesignId/DisplayRank/IsActive.
  async getTopicConceptMappings(inputs: TopicConceptMappingInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<TopicConceptMappingDisplay>>(
        `content-read/topics/concept-mappings`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Nested chapters for a set of CourseSubjectIds — each chapter carries SubjectChapterId
  // (junction) plus Class/Module so the selector can group by Class × Module.
  async getSubjectChildrenNested(inputs: SubjectChildrenInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<SubjectChildrenNestedDisplay>>(
        `content-read/subjects/children-nested`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Nested topics for a set of SubjectChapterIds — each topic carries ChapterTopicId (junction).
  async getChapterChildrenNested(inputs: ChapterChildrenInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<ChapterChildrenNestedDisplay>>(
        `content-read/chapters/children-nested`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Topic options for one chapter — IdNamePair rows whose Id is the ChapterTopicId junction.
  async getTopicsForSubjectChapter(
    subjectChapterId: number,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/subject-chapters/${subjectChapterId}/topics`,
        { params: { onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // AR-13.5: returns TopicConcept[] (ConceptId/ConceptName/TopicConceptId/IsActive), not
  // IdNamePair — the TopicConceptId junction feeds the activity question-picker flow.
  async getConceptsForTopic(chapterTopicId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<TopicConcept>>(
        `content-read/chapter-topics/${chapterTopicId}/concepts`,
        { params: { onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Topic→concept tree for one chapter (moved off legacy content/nested-concepts-for-chapter;
  // the flat IdNameChildren rows became ChapterTopicConceptsNestedDisplay). Topics with no
  // mapped concepts come back with an empty Concepts list.
  async getNestedConceptsForSubjectChapter(
    subjectChapterId: number,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ChapterTopicConceptsNestedDisplay>>(
        `content-read/subject-chapters/${subjectChapterId}/nested-concepts`,
        { params: { onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // AR-13.7: ID-based topic details (docs/api/content-read.json topics/{topicId}/details).
  async getTopicDetails(topicId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<TopicDetails>(
        `content-read/topics/${topicId}/details`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // AR-13.7: ID-based concept details (docs/api/content-read.json concepts/{conceptId}/details).
  async getConceptDetails(conceptId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ConceptDetails>(
        `content-read/concepts/${conceptId}/details`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // AR-13.1: paginated review read (docs/api/content-read.json
  // subject-chapters/{subjectChapterId}/questions-for-review). pageNumber/pageSize are
  // required; the response is the PaginatedResponse envelope (unwrapped via isPaginated).
  async getChapterQuestionsForReview(
    subjectChapterId: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<QuestionByChapterConcept>>(
        `content-read/subject-chapters/${subjectChapterId}/questions-for-review`,
        { params: { pageNumber: pageNumber, pageSize: pageSize } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // AR-13.2: content-name search is split per resource in the doc
  // (docs/api/content-read.json `/content-read/{resource}/search`). One explicit method per
  // endpoint — no runtime resource map — so each path is greppable and individually tested;
  // `searchText` is documented as array[string] on all eight searches, so the param accepts an
  // array for multi-term search; a bare string still works via the backend's list coercion and
  // is serialized as a single value by the shared paramsSerializer (src/services/apiVerified.ts).
  // the call site picks the method for the selected content type. Server-paginated (unwrap via `true`).
  async searchCourses(
    searchText: string | Array<string>,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<ContentData>>(
        `content-read/courses/search`,
        { params: { searchText: searchText, pageNumber: pageNumber, pageSize: pageSize } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async searchClasses(
    searchText: string | Array<string>,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<ContentData>>(
        `content-read/classes/search`,
        { params: { searchText: searchText, pageNumber: pageNumber, pageSize: pageSize } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async searchSubjects(
    searchText: string | Array<string>,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<ContentData>>(
        `content-read/subjects/search`,
        { params: { searchText: searchText, pageNumber: pageNumber, pageSize: pageSize } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async searchModules(
    searchText: string | Array<string>,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<ContentData>>(
        `content-read/modules/search`,
        { params: { searchText: searchText, pageNumber: pageNumber, pageSize: pageSize } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async searchChapters(
    searchText: string | Array<string>,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<ContentData>>(
        `content-read/chapters/search`,
        { params: { searchText: searchText, pageNumber: pageNumber, pageSize: pageSize } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async searchTopics(
    searchText: string | Array<string>,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<ContentData>>(
        `content-read/topics/search`,
        { params: { searchText: searchText, pageNumber: pageNumber, pageSize: pageSize } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async searchConcepts(
    searchText: string | Array<string>,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<ContentData>>(
        `content-read/concepts/search`,
        { params: { searchText: searchText, pageNumber: pageNumber, pageSize: pageSize } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async searchQuestions(
    searchText: string | Array<string>,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<QuestionSearchResultDisplay>>(
        `content-read/questions/search`,
        { params: { searchText: searchText, pageNumber: pageNumber, pageSize: pageSize } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllClasses(onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(`content-read/classes`, {
        params: { onlyActive: onlyActive },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllModules(onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(`content-read/modules`, {
        params: { onlyActive: onlyActive },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllSubjects(onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(`content-read/subjects`, {
        params: { onlyActive: onlyActive },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllChapters(onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(`content-read/chapters`, {
        params: { onlyActive: onlyActive },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllTopics(onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(`content-read/topics`, {
        params: { onlyActive: onlyActive },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllConcepts(onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(`content-read/concepts`, {
        params: { onlyActive: onlyActive },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // content-read/{type}/names — batch id→name resolution for a known set of ids.
  // One explicit method per endpoint (mirrors the search family); the array of ids
  // serializes to repeated `{type}Ids=` params via the apiVerified paramsSerializer.
  async getCourseNames(courseIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/courses/names`,
        {
          params: { courseIds: courseIds },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getClassNames(classIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/classes/names`,
        {
          params: { classIds: classIds },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getModuleNames(moduleIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/modules/names`,
        {
          params: { moduleIds: moduleIds },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSubjectNames(subjectIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/subjects/names`,
        { params: { subjectIds: subjectIds } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getChapterNames(chapterIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/chapters/names`,
        { params: { chapterIds: chapterIds } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTopicNames(topicIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/topics/names`,
        {
          params: { topicIds: topicIds },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getConceptNames(conceptIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/concepts/names`,
        { params: { conceptIds: conceptIds } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSubjectChapterNames(subjectChapterIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-read/subject-chapters/names`,
        { params: { subjectChapterIds: subjectChapterIds } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Unpaginated course-design search (newest maxResults, lecture-designs precedent);
  // query → params, bare array response.
  async getCourseDesigns(query: ContentDesignSearchQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<CourseDesignDisplay>>(
        `content-read/course-designs`,
        { params: query },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Unpaginated subject-design search (newest maxResults, lecture-designs precedent);
  // query → params, bare array response.
  async getSubjectDesigns(query: ContentDesignSearchQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SubjectDesignDisplay>>(
        `content-read/subject-designs`,
        { params: query },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * Unified subject mapping-search backing the "Add Subject" modal: ONE term list matched against
   * both subject names and design tags/summaries, returning matching Subjects + Designs + the
   * already-used Subject×Design combos (RecommendedCombos) in a single call. The combos carry the
   * MASTER SubjectId, which getSubjectDesigns' UsedIn cannot give (it only has CourseSubjectId).
   * Unpaginated, capped per section by maxResults. Contract:
   * `plans/content-mapping-search/backend-handover.md`.
   */
  async getSubjectMappingSearch(query: SubjectMappingSearchQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SubjectMappingSearchDisplay>(
        `content-read/subjects/mapping-search`,
        { params: query },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /** Chapter counterpart of getSubjectMappingSearch — combos also pin down Class (and Module). */
  async getChapterMappingSearch(query: ChapterMappingSearchQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ChapterMappingSearchDisplay>(
        `content-read/chapters/mapping-search`,
        { params: query },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /** Topic counterpart of getSubjectMappingSearch. */
  async getTopicMappingSearch(query: TopicMappingSearchQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<TopicMappingSearchDisplay>(
        `content-read/topics/mapping-search`,
        { params: query },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /** Concept counterpart of getSubjectMappingSearch. */
  async getConceptMappingSearch(query: ConceptMappingSearchQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ConceptMappingSearchDisplay>(
        `content-read/concepts/mapping-search`,
        { params: query },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Unpaginated chapter-design search (newest maxResults, lecture-designs precedent);
  // query → params, bare array response.
  async getChapterDesigns(query: ContentDesignSearchQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ChapterDesignDisplay>>(
        `content-read/chapter-designs`,
        { params: query },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Designs already mapped to a subject/chapter master (distinct design ids across its junction
  // rows) — feeds the "reuse an existing design" picker. Plain list, each carrying UsedIn.
  async getUsedDesignsForSubject(subjectId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SubjectDesignDisplay>>(
        `content-read/subjects/${subjectId}/used-designs`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getUsedDesignsForChapter(chapterId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ChapterDesignDisplay>>(
        `content-read/chapters/${chapterId}/used-designs`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getUsedDesignsForTopic(topicId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<TopicDesignDisplay>>(
        `content-read/topics/${topicId}/used-designs`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getUsedDesignsForConcept(conceptId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ConceptDesignDisplay>>(
        `content-read/concepts/${conceptId}/used-designs`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Unpaginated topic-design search (newest maxResults, lecture-designs precedent);
  // query → params, bare array response.
  async getTopicDesigns(query: ContentDesignSearchQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<TopicDesignDisplay>>(
        `content-read/topic-designs`,
        { params: query },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Unpaginated concept-design search (newest maxResults, lecture-designs precedent);
  // query → params, bare array response.
  async getConceptDesigns(query: ContentDesignSearchQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ConceptDesignDisplay>>(
        `content-read/concept-designs`,
        { params: query },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Paginated question-design search; query → params, PaginatedResponse envelope unwrapped.
  async getQuestionDesigns(query: QuestionDesignSearchQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<QuestionDesignDisplay>>(
        `content-read/question-designs`,
        { params: query },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Lecture-design search. NOT paginated (like the five entity-design reads above): filters
  // are applied server-side and the response is a bare array of at most `maxResults` latest designs.
  // `search` is a LIST of tags, serialized as repeated `search=` params.
  async getLectureDesigns(query: LectureDesignSearchQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<LectureDesignDisplay>>(
        `content-read/lecture-designs`,
        { params: query },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Full design doc (pages/pdf-urls/tags/provenance) plus the Lectures usage array —
  // each linked library lecture with its name/level/focus and used-in breadcrumbs.
  async getLectureDesignDetail(lectureDesignId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<LectureDesignDetailDisplay>(
        `content-read/lecture-designs/${lectureDesignId}`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Video-design search — same contract as getLectureDesigns above (unpaginated, capped array).
  async getVideoDesigns(query: VideoDesignSearchQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<VideoDesignDisplay>>(
        `content-read/video-designs`,
        { params: query },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Teaching-aid-design search — same contract as getVideoDesigns (unpaginated, capped array).
  async getTeachingAidDesigns(query: TeachingAidDesignSearchQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<TeachingAidDesignDisplay>>(
        `content-read/teaching-aid-designs`,
        { params: query },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
